// Fase 7A — Modal condiviso "Programma richiamo".
// Usato IDENTICO dal Contact Drawer e dalla Lead Inbox: tenant/client/case
// arrivano dal contesto, la receptionist NON li riseleziona.
import { useEffect, useMemo, useState } from "react";
import { X, PhoneCall, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cbApiErr, fmtDateTime, notifyCallbackGoogleSync } from "@/lib/callbacks";

function pad(n) { return String(n).padStart(2, "0"); }
function toLocalInput(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function roundToNext(mins = 15) {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil(d.getMinutes() / mins) * mins);
  return d;
}

const DURATIONS = [10, 15, 20, 30, 45, 60];

export default function CallbackModal({ tenant, clientId, clientName, caseObj, defaultDuration = 20, onClose, onSaved }) {
  const [when, setWhen] = useState(() => toLocalInput(roundToNext(15)));
  const [duration, setDuration] = useState(defaultDuration);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const scheduledDate = useMemo(() => (when ? new Date(when) : null), [when]);

  const submit = async () => {
    if (!scheduledDate || isNaN(scheduledDate.getTime())) { toast.error("Seleziona data e ora"); return; }
    setSaving(true); setErrMsg(null);
    try {
      const payload = {
        tenant_id: tenant?.id,
        client_id: clientId,
        agency_case_id: caseObj?.id || null,
        scheduled_at: scheduledDate.toISOString(),
        duration_minutes: Number(duration),
        source: "manual",
      };
      const r = await api.post("/agency/callbacks", payload);
      notifyCallbackGoogleSync(r.data?.google_sync_status);
      onSaved?.(r.data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const reason = detail?.reason;
      const msg = detail?.message || cbApiErr(e, "Errore creazione richiamo");
      if (e?.response?.status === 409 || e?.response?.status === 503) {
        setErrMsg({ reason, msg });
        toast.error(msg);
      } else {
        toast.error(msg);
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose} data-testid="callback-modal">
      <div className="sp-card w-full max-w-md p-7 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-2xl font-black flex items-center gap-2">
            <PhoneCall className="h-5 w-5 text-[color:var(--sp-brand)]" /> Programma richiamo
          </h3>
          <button onClick={onClose} data-testid="callback-modal-close" className="h-7 w-7 rounded flex items-center justify-center hover:bg-[color:var(--sp-subtle)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="text-sm text-[color:var(--sp-text-muted)] mb-5">
          {clientName || "Contatto"}{tenant?.name ? ` · ${tenant.name}` : ""}
        </div>

        <label className="text-sm font-bold">Data e ora</label>
        <input type="datetime-local" data-testid="callback-datetime-input" className="sp-input mt-1"
          value={when} onChange={(e) => { setWhen(e.target.value); setErrMsg(null); }} />

        <label className="text-sm font-bold mt-4 block">Durata</label>
        <div className="flex flex-wrap gap-2 mt-1">
          {DURATIONS.map((m) => (
            <button key={m} type="button" data-testid={`callback-duration-${m}`}
              onClick={() => setDuration(m)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border ${Number(duration) === m ? "bg-[color:var(--sp-brand)] text-white border-[color:var(--sp-brand)]" : "border-[color:var(--sp-border)]"}`}>
              {m} min
            </button>
          ))}
        </div>

        {/* Orario finale sempre visibile */}
        <div className="mt-5 rounded-xl bg-[color:var(--sp-subtle)] border border-[color:var(--sp-border)] p-3 flex items-center gap-2 text-sm" data-testid="callback-summary">
          <Clock className="h-4 w-4 text-[color:var(--sp-brand)]" />
          <span className="font-bold">{scheduledDate && !isNaN(scheduledDate.getTime()) ? fmtDateTime(scheduledDate.toISOString()) : "—"}</span>
          <span className="text-[color:var(--sp-text-muted)]">· {duration} min</span>
        </div>

        {errMsg && (
          <div className="mt-3 rounded-xl border border-rose-300 bg-rose-50 p-3 text-xs text-rose-800" data-testid="callback-error">
            <div className="font-bold flex items-center gap-1"><AlertTriangle className="h-4 w-4" />
              {errMsg.reason === "google_unavailable" || errMsg.reason === "google_reconnect" ? "Google Calendar non verificabile" : "Orario non disponibile"}
            </div>
            <div className="opacity-90 mt-1">{errMsg.msg}</div>
            <div className="opacity-70 mt-0.5">
              {errMsg.reason === "google_unavailable" ? "Riprova tra poco." : errMsg.reason === "google_reconnect" ? "Ricollega Google Calendar dalle impostazioni." : "Scegli un altro orario."}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-5">
          <button type="button" onClick={onClose} className="sp-btn-ghost">Annulla</button>
          <button type="button" data-testid="callback-save-btn" onClick={submit} disabled={saving}
            className="sp-btn-primary flex-1 disabled:opacity-50">{saving ? "Salvataggio…" : "Programma richiamo"}</button>
        </div>
      </div>
    </div>
  );
}
